import { Button, Divider, Select, Spin } from 'antd';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Query } from 'src/core/stores/data-store';
import { validateEmail } from 'src/core/utils/object';
import { container } from 'src/inversify.config';
import { UserSummaryStore } from 'src/stores/users-store';

const UsersSelect: FC<
  {
    onChange: (values: { value: string; label: string }[]) => void;
    exclude?: string[];
    values?: { value: string; label: string }[];
    organizationFilter?: string;
    allowInviteUser?: boolean;
    placeholder?: string;
  } & WithTranslation
> = ({ t, onChange, exclude, values, organizationFilter, allowInviteUser = false, placeholder }) => {
  const userStore = useMemo(() => container.get(UserSummaryStore), []);
  const usersState = userStore.state;
  const [currentValues, setValues] = useState((values || []).map((x) => ({ value: x.value, label: x.label || `User ${x.value}` })));
  const [options, setOptions] = useState<{ key: string; value: string; label: string; email: string }[]>([]);
  const [currentEmail, setCurrentEmail] = useState<string>();
  const [invalidEmailWarningVisible, setInvalidEmailWarningVisible] = useState<boolean>(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadUsersData = async () => {
      let filter: any[] = (exclude || []).length === 0 ? [] : [`id ne ${exclude?.join(' and id ne ')}`];
      if (organizationFilter != null)
        filter = [...filter, { Organizations: { any: { OrganizationId: { value: organizationFilter, type: 'guid' } } } }];

      await userStore.load({
        orderBy: [{ field: 'DisplayName', direction: 'Ascending', useProfile: true }],
        skip: 0,
        take: 1000000,
        filter,
      } as Query);
      setIsLoading(false);
      setOptions(usersState.items.value.map((v) => ({ key: v.id, value: v.id, label: v.displayName, email: v.email })));
    };

    loadUsersData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Spin spinning={isLoading}>
      <Select
        classNames={{ popup: { root: 'users-select' } }}
        open={popupOpen}
        onOpenChange={(open) => setPopupOpen(open)}
        mode="multiple"
        loading={!!usersState.isBusy.get()}
        labelInValue
        onChange={(values) => {
          setValues(values);
          onChange(values);
        }}
        placeholder={placeholder || t(allowInviteUser ? 'SelectOrInviteUsers' : 'SelectUsers')}
        style={{ width: '100%', height: '100%' }}
        onSearch={(search) => {
          let searchIsNewEmail = true;
          if (!validateEmail(search)) searchIsNewEmail = false;
          searchIsNewEmail = searchIsNewEmail && usersState.items.value.every((user) => user.email !== search);
          searchIsNewEmail = searchIsNewEmail && options.every((option) => option.value !== search);
          if (searchIsNewEmail) {
            setCurrentEmail(search);
            setInvalidEmailWarningVisible(false);
          } else {
            if (!options.some((o) => o.email.toLowerCase().includes(search.toLowerCase()))) {
              setInvalidEmailWarningVisible(true);
            } else {
              setInvalidEmailWarningVisible(false);
            }
            setCurrentEmail(undefined);
          }
        }}
        filterOption={(input, option) =>
          (option?.label ?? '').toLowerCase().includes(input.toLowerCase()) || (option?.email ?? '').toLowerCase().includes(input.toLowerCase())
        }
        allowClear
        showSearch
        value={currentValues}
        options={options}
        popupRender={(menu) => {
          return (
            <>
              {invalidEmailWarningVisible && (
                <>
                  <span
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  >
                    {t('PleaseEnterAValidEmail')}
                  </span>
                </>
              )}
              {currentEmail && (
                <Button
                  className="add-email-button"
                  type="text"
                  style={{ paddingTop: 10, paddingBottom: 10, width: '100%', textAlign: 'left' }}
                  onMouseDown={(e) => {
                    setOptions([...options, { key: currentEmail, value: currentEmail, label: currentEmail, email: currentEmail }]);
                    const newValues = [...(currentValues ?? []), { label: currentEmail, value: currentEmail }];
                    setValues(newValues);
                    onChange(newValues);
                    setCurrentEmail(undefined);
                  }}
                >
                  <strong>{t('AddEmail')}:</strong> {currentEmail}
                </Button>
              )}

              <Divider style={{ marginTop: 2, marginBottom: 2 }} />
              {menu}
            </>
          );
        }}
      />
    </Spin>
  );
};

export default withTranslation()(UsersSelect);
